import { GiphyFetch } from '@giphy/js-fetch-api'
import { IGif } from '@giphy/js-types'
import type { GifOverlayProps } from '@giphy/react-components'
import { useContext, useRef } from 'react'
import { UGEContext } from '../../../context'
import { getStickerLayer } from '../../../utilities/layer'
import { Grid } from './style'

type Props = {
    className?: string
    searchTerm: string
}

const COMPACT_GRID_COLUMNS = 3
const DEFAULT_GRID_COLUMNS = 6
const COMPACT_GRID_GUTTER = 18
const DEFAULT_GRID_GUTTER = 30

const UGEStickersEditorGrid = ({ className, searchTerm }: Props) => {
    const {
        addLayer,
        allowedEditors,
        apiKey,
        crop,
        dimensions,
        fetchFavoriteStickers,
        layers,
        maxPreviewWidth,
        trackGAEvent,
    } = useContext(UGEContext)
    const giphyFetchRef = useRef<GiphyFetch>(new GiphyFetch(apiKey))
    const isCompact = maxPreviewWidth < 500
    const columns = isCompact ? COMPACT_GRID_COLUMNS : DEFAULT_GRID_COLUMNS
    const gutter = isCompact ? COMPACT_GRID_GUTTER : DEFAULT_GRID_GUTTER
    const singleStickerMode = allowedEditors.length === 1 && allowedEditors[0] === 'stickers'
    const selectedGifId = layers.find((layer) => layer.type === 'sticker' || layer.type === 'gif')?.gifId

    async function fetchGifs(offset: number) {
        if (fetchFavoriteStickers && searchTerm === 'giphyfavorites') {
            return fetchFavoriteStickers(offset)
        }

        if (searchTerm === 'giphytrending') {
            return giphyFetchRef.current.trending({ offset, type: 'stickers' })
        }

        if (searchTerm === 'emoji') {
            return giphyFetchRef.current.emoji({ offset })
        }

        return giphyFetchRef.current.search(searchTerm, { offset, type: 'stickers' })
    }

    async function onGifClick(gif: IGif) {
        const layer = await getStickerLayer(gif, searchTerm, dimensions, crop)

        addLayer(layer)

        trackGAEvent('creation_asset_add', {
            content: {
                contentId: gif.id,
                contentType: gif.is_sticker ? 'sticker' : 'gif',
            },
        })
    }

    const StickerSelectionOverlay = ({ gif }: GifOverlayProps) => {
        if (`${gif.id}` !== selectedGifId) {
            return null
        }

        return (
            <div
                aria-hidden
                style={{
                    border: '3px solid #d935d2',
                    borderRadius: 4,
                    boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.24)',
                    inset: 0,
                    pointerEvents: 'none',
                    position: 'absolute',
                }}
            />
        )
    }

    return (
        <Grid
            borderRadius={0}
            className={className}
            fetchGifs={fetchGifs}
            hideAttribution
            key={searchTerm}
            noLink
            user={{}}
            noResultsMessage="No stickers found"
            columns={columns}
            gutter={gutter}
            overlay={singleStickerMode ? StickerSelectionOverlay : undefined}
            width={maxPreviewWidth - (isCompact ? 30 : 60)}
            onGifClick={onGifClick}
        />
    )
}

export default UGEStickersEditorGrid
